import { Routes } from '@angular/router';
import { CampaignFormComponent } from '@campaign-form/campaign-form.component';
import { authGuard } from '@core/guards/auth.guard';
import { campaignFormLeaveGuard } from '@core/guards/campaign-form.guard';
import { createPasswordGuard } from '@core/guards/create-password.guard';
import { maintenanceGuard } from '@core/guards/maintenance.guard';
import { notFoundAccessGuard, notFoundLeaveGuard } from '@core/guards/not-found.guard';
import { permissionGuard } from '@core/guards/permission.guard';
import { validUuidParamGuard } from '@core/guards/valid-uuid-param.guard';
import { EPermissions } from '@enums/permissions.enum';
import { AdminCampaignsComponent } from '@pages/admin/admin-campaigns/admin-campaigns.component';
import { AdminEventAnalyticsComponent } from '@pages/admin/admin-event-analytics/admin-event-analytics.component';
import { AdminNewsComponent } from '@pages/admin/admin-news/admin-news.component';
import { AdminUserFeedbackComponent } from '@pages/admin/admin-user-feedback/admin-user-feedback.component';
import { AdminUserGroupComponent } from '@pages/admin/admin-user-group/admin-user-group.component';
import { CreatePasswordComponent } from '@pages/auth/create-password/create-password.component';
import { ForgotPasswordComponent } from '@pages/auth/forgot-password/forgot-password.component';
import { LoginComponent } from '@pages/auth/login/login.component';
import { NewPasswordComponent } from '@pages/auth/new-password/new-password.component';
import { CampaignDetailsComponent } from '@pages/campaigns/campaign-details/campaign-details.component';
import { CampaignsComponent } from '@pages/campaigns/campaigns.component';
import { CreatorDetailsComponent } from '@pages/creators/creator-details/creator-details.component';
import { CreatorsComponent } from '@pages/creators/creators.component';
import { CustomerDetailsComponent } from '@pages/customers/customer-details/customer-details.component';
import { CustomersComponent } from '@pages/customers/customers.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { AcceptCreatorInvitationComponent } from '@pages/invitation/accept-creator-invitation/accept-creator-invitation.component';
import { MaintenanceComponent } from '@pages/maintenance/maintenance.component';
import { NotFoundComponent } from '@pages/not-found/not-found.component';
import { ProfileComponent } from '@pages/profile/profile.component';
import { SettingsComponent } from '@pages/settings/settings.component';
import { SETTINGS_ACCESS_PERMISSIONS } from '@shared/constants/settings-access.constants';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  { canActivate: [createPasswordGuard], component: LoginComponent, path: 'auth/login' },
  { component: ForgotPasswordComponent, path: 'auth/forgot-password' },
  { component: NewPasswordComponent, path: 'auth/new-password' },
  {
    canActivate: [authGuard, permissionGuard],
    component: DashboardComponent,
    data: {
      permissions: [EPermissions.newsReadAll, EPermissions.newsReadOwn],
      redirectTo: '/campaigns',
    },
    path: 'dashboard',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: AdminCampaignsComponent,
    data: {
      permissions: [EPermissions.campaignAnalyticsReadAll, EPermissions.campaignReadAll],
      redirectTo: '/annotation',
    },
    path: 'admin/campaigns',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: AdminUserFeedbackComponent,
    data: {
      permissions: [EPermissions.userFeedbackReadAll, EPermissions.userReadAll],
      redirectTo: '/dashboard',
      requireAll: true,
    },
    path: 'admin/user-feedback',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: AdminUserGroupComponent,
    data: {
      permissions: [EPermissions.userGroupReadAll, EPermissions.userReadAll],
      redirectTo: '/dashboard',
    },
    path: 'admin/user-group',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: AdminNewsComponent,
    data: {
      permissions: [EPermissions.newsReadAll],
      redirectTo: '/dashboard',
    },
    path: 'admin/news',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: AdminEventAnalyticsComponent,
    data: {
      permissions: [EPermissions.userReadAll],
      redirectTo: '/dashboard',
    },
    path: 'admin/usage-adoption',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: CampaignsComponent,
    data: {
      permissions: [EPermissions.campaignReadOwn],
      redirectTo: '/creators',
      requireAll: true,
    },
    path: 'campaigns',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard, validUuidParamGuard],
    canDeactivate: [campaignFormLeaveGuard],
    component: CampaignFormComponent,
    data: {
      permissions: [EPermissions.campaignUpdateOwn, EPermissions.campaignUpdateAll],
      redirectTo: '/annotation',
      uuidRedirectTo: 'campaigns',
    },
    path: 'campaigns/form/:id',
    pathMatch: 'full',
  },
  {
    // Demo-mode counterpart to campaigns/export/:exportId below - see
    // demo-campaign-details.routes.ts's DEMO_CAMPAIGN_EXPORT_DETAILS_ROUTES doc comment. Reached
    // by "Copy link"/preview on the campaign export dialog whenever it's opened from inside
    // campaigns/demo/ (campaign-export-panel.component.ts's generateLink()). Ordered BEFORE the
    // shorter campaigns/demo/:id route below - that route has no top-level pathMatch:'full' (its
    // match only finalizes one level down, inside the lazy-loaded child), so putting the more
    // specific literal-segment route first avoids depending on router backtracking to reach this
    // one for a campaigns/demo/export/... URL.
    loadChildren: () =>
      import('@pages/campaigns/campaign-details/demo/demo-campaign-details.routes').then(
        (m) => m.DEMO_CAMPAIGN_EXPORT_DETAILS_ROUTES,
      ),
    path: 'campaigns/demo/export/:exportId',
  },
  {
    // Demo-mode counterpart to campaigns/briefing/creator/:briefingId below - see
    // demo-campaign-details.routes.ts's DEMO_CAMPAIGN_BRIEFING_DETAILS_ROUTES doc comment. Reached
    // by "Open"/"Copy link" on a creator's briefing menu whenever it's opened from inside
    // campaigns/demo/ (creator-campaign-export.component.ts's getBriefingUrl()). Same ordering
    // reasoning as campaigns/demo/export/:exportId above.
    loadChildren: () =>
      import('@pages/campaigns/campaign-details/demo/demo-campaign-details.routes').then(
        (m) => m.DEMO_CAMPAIGN_BRIEFING_DETAILS_ROUTES,
      ),
    path: 'campaigns/demo/briefing/creator/:briefingId',
  },
  {
    // Everything this route needs (component, providers, guards) lives behind this dynamic
    // import - see demo-campaign-details.routes.ts's doc comment for why that matters here
    // specifically (the demo's fixture data can run tens of MB once a real campaign is recorded,
    // and this keeps it out of the app's initial bundle entirely).
    loadChildren: () =>
      import('@pages/campaigns/campaign-details/demo/demo-campaign-details.routes').then(
        (m) => m.DEMO_CAMPAIGN_DETAILS_ROUTES,
      ),
    path: 'campaigns/demo/:id',
  },
  {
    canActivate: [authGuard, permissionGuard, validUuidParamGuard],
    component: CampaignDetailsComponent,
    data: {
      permissions: [EPermissions.campaignReadOwn, EPermissions.campaignReadAll],
      uuidRedirectTo: 'campaigns',
    },
    path: 'campaigns/:id',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard, validUuidParamGuard],
    component: CampaignDetailsComponent,
    data: {
      adminMode: true,
      permissions: [EPermissions.campaignReadAll],
      uuidRedirectTo: 'admin/campaigns',
    },
    path: 'admin/campaigns/:id',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: CreatorsComponent,
    data: {
      permissions: [EPermissions.creatorReadOwn],
      redirectTo: '/customers',
    },
    path: 'creators',
    pathMatch: 'full',
  },
  {
    // Everything this route needs (component, providers, guards) lives behind this dynamic
    // import - see demo-creator-details.routes.ts's doc comment for why that matters here
    // specifically. Ordered before the real creators/:id route below for the same reason
    // campaigns/demo/:id precedes campaigns/:id: this route has no top-level pathMatch:'full'
    // (its match only finalizes one level down, inside the lazy-loaded child).
    loadChildren: () =>
      import('@pages/creators/creator-details/demo/demo-creator-details.routes').then(
        (m) => m.DEMO_CREATOR_DETAILS_ROUTES,
      ),
    path: 'creators/demo/:id',
  },
  {
    canActivate: [authGuard, permissionGuard, validUuidParamGuard],
    component: CreatorDetailsComponent,
    data: {
      permissions: [EPermissions.creatorReadOwn, EPermissions.creatorReadAll],
      redirectTo: '/creator',
      uuidRedirectTo: 'creators',
    },
    path: 'creators/:id',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: CreatorDetailsComponent,
    data: {
      creatorMode: true,
      permissions: [EPermissions.creatorReadSelf],
      redirectTo: '/profile',
    },
    path: 'creator',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: CustomersComponent,
    data: {
      permissions: [EPermissions.customerReadOwn],
      redirectTo: '/creator',
    },
    path: 'customers',
    pathMatch: 'full',
  },
  {
    // Everything this route needs (component, providers, guards) lives behind this dynamic
    // import - see demo-customer-details.routes.ts's doc comment. Ordered before the real
    // customers/:id route below, same reason as the creators/customers demo route above.
    loadChildren: () =>
      import('@pages/customers/customer-details/demo/demo-customer-details.routes').then(
        (m) => m.DEMO_CUSTOMER_DETAILS_ROUTES,
      ),
    path: 'customers/demo/:id',
  },
  {
    canActivate: [authGuard, permissionGuard, validUuidParamGuard],
    component: CustomerDetailsComponent,
    data: {
      permissions: [EPermissions.customerReadOwn],
      redirectTo: '/creator',
      uuidRedirectTo: 'customers',
    },
    path: 'customers/:id',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard],
    component: ProfileComponent,
    path: 'profile',
    pathMatch: 'full',
  },
  {
    canActivate: [authGuard, permissionGuard],
    component: SettingsComponent,
    data: {
      // Kept in sync with the settings sidebar's own per-tab gates (see
      // SettingsSidebarComponent.items and settings.component.html) - a user needs at least one of
      // these to see anything at all on this page (userGroupDeleteMemberOwn -> User Management,
      // campaignCreate -> Global Templates), so this list must be updated alongside those - see
      // SETTINGS_ACCESS_PERMISSIONS's own doc comment for the other places sharing this constant.
      permissions: SETTINGS_ACCESS_PERMISSIONS,
    },
    path: 'account-panel',
    pathMatch: 'full',
  },
  // This route was renamed from 'settings' to 'account-panel' - kept as a redirect so old
  // bookmarks/links (and anything hardcoded elsewhere pointing at the old path) still resolve.
  { path: 'settings', pathMatch: 'full', redirectTo: 'account-panel' },
  {
    canActivate: [authGuard],
    component: CreatePasswordComponent,
    path: 'create-password',
    pathMatch: 'full',
  },
  /* Non-authorized routes */
  {
    canActivate: [createPasswordGuard, validUuidParamGuard],
    component: CampaignDetailsComponent,
    data: {
      exportMode: true,
      uuidParamName: 'exportId',
      uuidRedirectTitleKey: 'pages.notFound.sharedTitle',
      uuidRedirectTo: '/not-found',
      uuidShowSnackbar: false,
    },
    path: 'campaigns/export/:exportId',
    pathMatch: 'full',
  },
  {
    canActivate: [createPasswordGuard, validUuidParamGuard],
    component: CampaignDetailsComponent,
    data: {
      creatorBriefingMode: true,
      uuidParamName: 'briefingId',
      uuidRedirectTitleKey: 'pages.notFound.sharedTitle',
      uuidRedirectTo: '/not-found',
      uuidShowSnackbar: false,
    },
    path: 'campaigns/briefing/creator/:briefingId',
    pathMatch: 'full',
  },
  {
    canActivate: [createPasswordGuard],
    component: AcceptCreatorInvitationComponent,
    path: 'invitations/:invitationId',
    pathMatch: 'full',
  },
  {
    canActivate: [createPasswordGuard, notFoundAccessGuard],
    canDeactivate: [notFoundLeaveGuard],
    component: NotFoundComponent,
    path: 'not-found',
    pathMatch: 'full',
  },
  {
    canActivate: [maintenanceGuard],
    component: MaintenanceComponent,
    path: 'maintenance',
    pathMatch: 'full',
  },
  // Wildcard route to handle all unmatched routes - MUST BE LAST
  { path: '**', redirectTo: 'dashboard' },
];
